import { constants } from '@clubmed/utils';
import { getKidsCivilities } from '@clubmed/domains-service';
import type { OpeningDate } from 'Store/calendar/calendar.helper';
import 'Store/calendar/calendar.helper';
import {
  EMAIL_OPTIN,
  PHONE_OPTIN,
  SMS_OPTIN,
  PROFILING,
} from 'Utils/legal-information/legalInformationIds.constants';
import type { DurationOptions } from 'Store/utils/selectOptionsBuilders';
import {
  getAdultsOptions,
  getChildrenOptions,
  getDurationOptions,
} from 'Store/utils/selectOptionsBuilders';
import {
  MAX_FORM_ADULTS,
  MIN_FORM_ADULTS,
  MAX_FORM_KIDS,
  MIN_FORM_KIDS,
} from 'Components/widgets/prebooking-form/PrebookingForm.constants';
import { isWinterMountainResort } from 'Utils/resort/resortUtils';
// Line props
export type LineProduct = {
  productId: string;
  sortedFirst?: boolean;
  departureDateStart?: string;
  departureDateEnd?: string;
  snow?: boolean;
  sun?: boolean;
};
export type PrebookingFormLineProps = {
  dateFormat: string;
  mandatoryFieldsInformation: string;
  preview: boolean;
  login: {
    displayLoginIcon: boolean;
    labelCTA: string;
    memberSpace: string;
  };
  stay: {
    title: string;
    productLabel: string;
    productPlaceholder: string;
    minAgeInformation: string;
    departureCityLabel: string;
    departureCityPlaceholder: string;
    departureDateLabel: string;
    departureDatePlaceholder: string;
    durationLabel: string;
    durationValue: PluralizedLabel;
    durationPlaceholder: string;
    accommodationsLabel: string;
    accommodationsCategoryPlaceholder: string;
    accommodationsTypePlaceholder: string;
    numberOfParticipantsLabel: string;
    adultsAge: PluralizedLabel;
    adultsInformation: string;
    childrenAge: PluralizedLabel;
    childrenInformation: string;
    numberOfAdultsLabel: string;
    numberOfKidsLabel: string;
    dateSkiResortInformation: string;
    safcResortInformation: string;
    colcResortInformation: string;
    cskcResortInformation: string;
    bokcResortInformation: string;
    tooManyPax: {
      title: string;
      text: string;
    };
  };
  mainTraveler: {
    title: string;
    mainTravelerInformation: string;
    civilityPlaceholder: string;
    firstNamePlaceholder: string;
    firstNameUnicodePlaceholder: string;
    lastNamePlaceholder: string;
    lastNameUnicodePlaceholder: string;
    birthDateInformation: string;
    gmNumberPlaceholder: string;
    gmNumberInformation: string;
    emailPlaceholder: string;
    emailInformation: string;
    phoneNumberPlaceholder: string;
    phoneNumberInformation: string;
    favoriteTravelAgencyLabel: string;
    favoriteTravelAgencyPlaceholder: string;
    optinCommunicationsInformation: string;
    optinEmailLabel: string;
    optinSMSLabel: string;
  };
  pax: {
    title: string;
    adultCompanionLabel: string;
    childCompanionLabel: string;
    childCompanionErrorLabel: string;
    childBirthDateInformationLabel_n: string;
  };
  estimativePrice: {
    labelCTA: string;
    loaderLabel: string;
    safcEstimativePrice: string;
  };
  sendRequest: string;
  continueRequest: string;
  modifyRequest: string;
  products: Array<LineProduct>;
  departureDateStart: string;
  departureDateEnd: string;
  submitError: string;
};
// Resolver props
export type TravelAgency = {
  id: string;
  name: string;
  address?: {
    city: string;
  };
};
type BookingCriteria = {
  durations: Array<number>;
  departureCities: Array<{
    name: string;
    value: string;
  }>;
};
type ResortAccommodation = {
  id: string;
  label: string;
  categories: Array<{
    id: string;
    label: string;
  }>;
};
type ResortOpeningDates = Array<OpeningDate>;
type Resort = {
  id: string;
  name: string;
  destination: {
    country: string;
  };
  accommodations: Array<ResortAccommodation>;
  openingDates: ResortOpeningDates;
  adultOnly: boolean;
  minAuthorizedAge: {
    formattedValue: string;
    value: number;
  };
  category: string;
};
type AccommodationRanking =
  | (typeof constants.RANKINGS)[keyof typeof constants.RANKINGS]
  | 'UNKNOWN';
type AccommodationCategory = {
  productId: string;
  accommodations: Array<{
    id: string;
    label: string;
    ranking: AccommodationRanking;
  }>;
};
export type LegalInformation = {
  id: string;
  title: string;
  description: string;
  preChecked: boolean;
};
export type PrebookingFormResolverProps = {
  config: {
    account: {
      authenticated: {
        greeting: string;
      };
    };
  };
  success: null | {
    url: string;
  };
  error: null | {
    url: string;
  };
  civilities: {
    adult: Array<{
      name: string;
      value: string;
    }>;
  };
  travelAgencies: null | Array<TravelAgency>;
  bookingsCriteria: Array<BookingCriteria>;
  resorts: Array<Resort>;
  accommodationCategories: Array<AccommodationCategory>;
  legalInformations: {
    bookingProcess: Array<LegalInformation>;
  };
};
// PrebookingForm props
export type FormFieldOption = {
  name: string;
  value: string;
};
type FormProductAccommodation = {
  name: string;
  value: string;
  rankingIndex: number;
  types: Array<FormFieldOption>;
};
export type FormProduct = {
  accommodations: Array<FormProductAccommodation>;
  adultOnly: boolean;
  departureDateStart?: string;
  departureDateEnd?: string;
  destination: {
    country: string;
  };
  durations: DurationOptions;
  minAuthorizedAge: string;
  minAuthorizedAgeInMonths: number;
  name: string;
  openingDates: ResortOpeningDates;
  productId: string;
  transports: Array<FormFieldOption>;
  isSki: boolean;
  snow?: boolean;
  sun?: boolean;
};
export type FormProducts = Record<string, FormProduct>;
type NewsletterOptin = null | {
  title: string;
  description: string;
  preChecked: boolean;
};
export type NewsletterOptins = {
  optinEmail: NewsletterOptin;
  optinPhone: NewsletterOptin;
  optinSms: NewsletterOptin;
  optinProfile: NewsletterOptin;
};
// CIVILITIES
export const getCivilities = (adultCivilities: Array<FormFieldOption>, locale: string) => ({
  adults: adultCivilities,
  kids: getKidsCivilities(locale).map(({ value, label }) => ({
    value,
    name: label,
  })),
});

// TRAVEL AGENCY OPTIONS
const getAgencyName = (travelAgency: TravelAgency) => {
  return `${travelAgency.address?.city || ''} - ${travelAgency.name}`;
};

const getFilteredAgencyName = (travelAgency: TravelAgency) => {
  /**
   * ^[.+?\s]*            Match 0 or more chars or white space at the beginning
   * (?:<T1>|<T2>)?       Do not capture group of 1st or 2nd potential terms
   * \s*                  Match 0 or more white spaces
   * (.+?)$               Capture group of any chars up to the end
   * /i                   Case insensitive Flags
   */
  const agencyNameFilter = /^[.+?\s]*(?:CLUB MED VOYAGES|APPARTEMENT BOUTIQUE)?\s*(.+?)$/i;
  const trimmedName = agencyNameFilter.exec(travelAgency.name);
  return trimmedName && trimmedName[1] ? trimmedName[1] : getAgencyName(travelAgency);
};

export const getTravelAgencyOptions = (
  travelAgencies: null | Array<TravelAgency>,
  locale: string,
): Array<FormFieldOption> => {
  if (!travelAgencies) {
    return [];
  }

  return travelAgencies
    .map((travelAgency) => ({
      name: locale === 'fr-FR' ? getFilteredAgencyName(travelAgency) : getAgencyName(travelAgency),
      value: travelAgency.id,
    }))
    .sort((travelAgencyA, travelAgencyB) =>
      travelAgencyA.name.localeCompare(travelAgencyB.name) > 0 ? 1 : -1,
    );
};
// ADULTS/KIDS NUMBER OPTIONS
export const getAdultsNumberOptions = (adultsAge: PluralizedLabel) =>
  getAdultsOptions(adultsAge, MIN_FORM_ADULTS, MAX_FORM_ADULTS).map<FormFieldOption>(
    ({ label, value }) => ({
      name: label,
      value: value.toString(),
    }),
  );
export const getKidsNumberOptions = (kidsAge: PluralizedLabel) =>
  getChildrenOptions(kidsAge, MIN_FORM_KIDS, MAX_FORM_KIDS).map<FormFieldOption>(
    ({ label, value }) => ({
      name: label,
      value: value.toString(),
    }),
  );

// NEWSLETTER OPTINS
const getNewsletterOptin = (optin: void | LegalInformation) => {
  return optin
    ? {
        title: optin.title,
        description: optin.description,
        preChecked: optin.preChecked,
      }
    : null;
};

export const getNewsletterOptins = (consentOptins: Array<LegalInformation>) => {
  const optinEmail = consentOptins.find((optin) => optin.id === EMAIL_OPTIN);
  const optinPhone = consentOptins.find((optin) => optin.id === PHONE_OPTIN);
  const optinSms = consentOptins.find((optin) => optin.id === SMS_OPTIN);
  const optinProfile = consentOptins.find((optin) => optin.id === PROFILING);
  return {
    optinEmail: getNewsletterOptin(optinEmail),
    optinPhone: getNewsletterOptin(optinPhone),
    optinSms: getNewsletterOptin(optinSms),
    optinProfile: getNewsletterOptin(optinProfile),
  };
};
// PRODUCT OPTIONS
export type ProductOption = {
  key: string;
  name: string;
  value: null | string;
  isDisabled?: boolean;
};

const getProductOption = (isTopProduct: boolean) => (product: FormProduct) => ({
  key: `${product.productId}-${isTopProduct ? 'top' : 'regular'}`,
  name: `${product.name}, ${product.destination.country}`,
  value: product.productId,
});

export function getProductOptions(
  lineProducts: Array<LineProduct>,
  products: FormProducts,
): Array<ProductOption> {
  const topProducts = lineProducts
    .filter((lineProduct) => lineProduct.sortedFirst)
    .map<FormProduct>(({ productId }) => products[productId])
    .filter(Boolean)
    .map<ProductOption>(getProductOption(true));
  return [
    ...topProducts,
    ...(topProducts.length
      ? [
          {
            key: 'separator',
            name: '──────────',
            value: null,
            isDisabled: true,
          },
        ]
      : []), // https://davidwalsh.name/flow-object-values
    ...(Object.values(products) as any)
      .map<ProductOption>(getProductOption(false))
      .sort((productOptionA, productOptionB) => {
        if (!productOptionA.name) {
          return 1;
        }

        if (!productOptionB.name) {
          return -1;
        }

        return productOptionA.name.localeCompare(productOptionB.name);
      }),
  ];
}
export type ProductFilter = 'sun' | 'snow';
export function filterProductsByType(products: FormProducts, filter: ProductFilter): FormProducts {
  const filtered = {};
  Object.keys(products).forEach((productId) => {
    const product = products[productId];

    // Strictly sun: sun=true AND snow=false
    if (filter === 'sun' && product.sun && !product.snow) {
      filtered[productId] = product;
    } // Strictly snow: snow=true AND sun=false
    else if (filter === 'snow' && product.snow && !product.sun) {
      filtered[productId] = product;
    }
  });
  return filtered;
}

// PRODUCT DATA
function getAccommodationsOptions(
  accommodations: Array<ResortAccommodation> = [],
  accommodationCategories: Array<{
    id: string;
    label: string;
    ranking: AccommodationRanking;
  }> = [],
): Array<FormProductAccommodation> {
  return accommodationCategories
    .map((accommodationCategory) => {
      const {
        id: categoryId,
        label: categoryLabel,
        ranking: categoryRanking,
      } = accommodationCategory;
      const types = accommodations
        .filter((accommodation) =>
          accommodation.categories.map(({ id }) => id).includes(categoryId),
        )
        .map(({ id, label }) => ({
          name: label,
          value: id,
        }));
      return {
        value: categoryId,
        name: categoryLabel,
        rankingIndex: Object.values(constants.RANKINGS).findIndex(
          (ranking) => ranking === categoryRanking,
        ),
        types,
      };
    })
    .sort(
      (accommodationsOptionA, accommodationsOptionB) =>
        accommodationsOptionB.rankingIndex - accommodationsOptionA.rankingIndex,
    );
}

type ProductInputs = {
  resorts: Array<Resort>;
  bookingsCriteria: Array<BookingCriteria>;
  accommodationCategories: Array<AccommodationCategory>;
  durationLabels: PluralizedLabel;
  lineProducts?: Array<LineProduct>;
};
export function getFormProducts({
  resorts,
  bookingsCriteria,
  accommodationCategories,
  durationLabels,
  lineProducts = [],
}: ProductInputs) {
  return resorts.reduce((products, product, productIndex) => {
    const { name, destination, adultOnly, openingDates, accommodations, minAuthorizedAge } =
      product;
    const bookingCriteria = bookingsCriteria?.[productIndex];
    const durations: Array<number> = bookingCriteria?.durations || [];
    const transports: Array<{
      value: string;
      name: string;
    }> = bookingCriteria?.departureCities || [];
    const productAccommodationCategories = accommodationCategories
      .filter(Boolean)
      .find((category) => category.productId === product.id);
    const categories: Array<{
      id: string;
      label: string;
      ranking: AccommodationRanking;
    }> = productAccommodationCategories?.accommodations || [];
    const lineProduct = lineProducts.find((lp) => lp.productId === product.id);
    const departureDateStart = lineProduct?.departureDateStart || '';
    const departureDateEnd = lineProduct?.departureDateEnd || '';
    const snow = lineProduct?.snow || false;
    const sun = lineProduct?.sun || false;
    products[product.id] = {
      productId: product.id,
      isSki: isWinterMountainResort(product),
      name,
      destination,
      openingDates,
      minAuthorizedAge: minAuthorizedAge.formattedValue || '',
      minAuthorizedAgeInMonths: minAuthorizedAge.value || '',
      durations: getDurationOptions(durationLabels, durations, 'count'),
      transports,
      accommodations: getAccommodationsOptions(accommodations, categories),
      adultOnly,
      departureDateStart,
      departureDateEnd,
      snow,
      sun,
    };
    return products;
  }, {});
}
